import { createContext, Component, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { some, uniq, without, isEmpty } from 'lodash';

import { shortUuidRegex } from 'utils/regexes';

const ScrollActiveOnPaths = [
  '/settings/integrations/\\w*',
  '/settings/\\w*',
  '/candidates$',
];

const DisableActiveOnPaths = [
  `^/settings/(members/invite|teams/(new|${shortUuidRegex}/edit))`,
  `^/settings/shared-lists/(appearances|custom-fields)/(new|${shortUuidRegex}/edit)`,
  `^/settings/jobs/appearances/(new|${shortUuidRegex}/(edit|duplicate))`,
  '^/settings/jobs/applicant-statuses',
  `^/(submission-lists|marketing-lists)/${shortUuidRegex}/candidates/(add|review|edit|activate|vms-submit|request|${shortUuidRegex})`,
  `^/(submission-lists|marketing-lists)/new/(job|marketing)`,
  `^/candidates$`,
  `^/candidates/(new|${shortUuidRegex}/profiles/(add|${shortUuidRegex}/(edit|export)))`,
  `^/clients/${shortUuidRegex}/contacts/(new|${shortUuidRegex}/edit)`,
  '^/jobs(/|$)',
  '^/integrations/bullhorn/widget',
];

const LAUNCHER_SELECTOR =
  '.intercom-app, .intercom-launcher, .intercom-launcher-frame, .intercom-launcher-badge-frame, .intercom-lightweight-app-launcher';

const LAUNCHER_HIDE_STYLE_ID = 'intercom-launcher-hide';

export const IntercomAPI = (...args) => {
  if (window.Intercom) {
    window.Intercom.apply(null, args);
  }
};

export const IntercomLauncherToggle = (hide) => {
  if (hide) {
    if (!document.getElementById(LAUNCHER_HIDE_STYLE_ID)) {
      const style = document.createElement('style');
      style.id = LAUNCHER_HIDE_STYLE_ID;
      style.textContent = `${LAUNCHER_SELECTOR} { position: absolute !important; z-index: -1 !important; opacity: 0 !important; }`;
      document.head.appendChild(style);
    }
  } else {
    document.getElementById(LAUNCHER_HIDE_STYLE_ID)?.remove();
  }
};

export const IntercomShowNewMessage = (message) => {
  IntercomLauncherToggle(false);
  IntercomAPI('show');
  setTimeout(() => {
    IntercomAPI('showNewMessage', message);
  }, 200);
};

export const Context = createContext({});

export const Provider = ({ children }) => {
  const [hidden, setHidden] = useState([]);
  const handleChangeHidden = (value, scope) => {
    const newHidden = value
      ? uniq(hidden.concat(scope))
      : without(hidden, scope);

    if (newHidden.length !== hidden.length) {
      IntercomLauncherToggle(!isEmpty(newHidden));
      setHidden(newHidden);
    }
  };

  return (
    <Context.Provider
      value={{
        hidden: !isEmpty(hidden),
        setHidden: handleChangeHidden,
      }}
    >
      {children}
    </Context.Provider>
  );
};

Provider.propTypes = {
  children: PropTypes.node,
};

class IntercomComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      track: false,
      isBottom: false,
    };
  }

  componentDidMount() {
    const { history, location } = this.props;

    this.unlisten = history.listen((newLocation) => {
      setTimeout(() => {
        this.setupTracking(newLocation);
        this.setupVisibility(newLocation);
      }, 100);
    });

    this.setupTracking(location);
    this.setupVisibility(location);
  }

  componentWillUnmount() {
    this.unlisten();
    IntercomAPI('shutdown');
  }

  setupTracking(location) {
    const { track } = this.state;
    const shouldTrack = this.shouldTrackScroll(location);

    if (shouldTrack && !track) {
      this.setState({ track: true, isBottom: false }, () => {
        document.addEventListener('scroll', this.trackScrolling);
      });
    } else if (!shouldTrack && track) {
      this.setState({ track: false, isBottom: false }, () => {
        this.moveIcon('2rem');
        document.removeEventListener('scroll', this.trackScrolling);
      });
    }
  }

  setupVisibility(location) {
    const { setHidden } = this.context;

    setHidden(this.shouldHide(location), 'self');
  }

  /* eslint no-param-reassign: 0 */
  moveIcon = (bottomPosition) => {
    const elements = document.querySelectorAll(LAUNCHER_SELECTOR);
    elements.forEach((el) => {
      if (el.style) {
        el.style.bottom = bottomPosition;
      }
    });
  };

  trackScrolling = () => {
    const { appContainer, scrollUp } = this.props;
    const { isBottom } = this.state;

    if (!appContainer) {
      return;
    }

    if (this.isBottom(appContainer)) {
      if (!isBottom) {
        this.moveIcon('10rem');
        this.setState({ isBottom: true });
      }
    } else if (isBottom) {
      if (!scrollUp) {
        this.moveIcon('2rem');
      }
      this.setState({ isBottom: false });
    }
  };

  shouldTrackScroll(location) {
    const { pathname } = location;

    return some(ScrollActiveOnPaths, (re) =>
      new RegExp(re, 'i').test(pathname)
    );
  }

  shouldHide(location) {
    const { pathname } = location;

    return some(DisableActiveOnPaths, (re) =>
      new RegExp(re, 'i').test(pathname)
    );
  }

  isBottom(el) {
    return (
      parseInt(el.getBoundingClientRect().bottom, 10) <= window.innerHeight
    );
  }

  render() {
    return null;
  }
}

IntercomComponent.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
  appContainer: PropTypes.object,
  scrollUp: PropTypes.bool,
};

IntercomComponent.contextType = Context;

export const Intercom = withRouter(IntercomComponent);
